/**
 * 更新浏览器标题和 favicon 的工具函数（共享版本）
 * 支持通过配置自定义默认标题和标题格式
 */

export interface DocumentTitleConfig {
  /** 默认标题 */
  defaultTitle: string;
  /** 标题格式函数，接收机构名称，返回完整标题 */
  formatTitle?: (orgName: string) => string;
  /** 默认 favicon 路径 */
  defaultFavicon?: string;
}

/**
 * 创建文档标题和 favicon 更新器
 * @param config 配置选项
 */
export function createDocumentTitleUpdater(config: DocumentTitleConfig) {
  const {
    defaultTitle,
    formatTitle = (orgName: string) => orgName, // 默认直接使用机构名称
    defaultFavicon = '/favicon.ico',
  } = config;

  /**
   * 更新浏览器标题
   * @param title 标题文本，如果为空则使用默认标题
   */
  function updateDocumentTitle(title?: string): void {
    if (title && title.trim()) {
      document.title = formatTitle(title);
    } else {
      document.title = defaultTitle;
    }
  }

  /**
   * 生成简单的 hash 版本号（基于字符串内容）
   * @param str 输入字符串
   * @returns hash 值（数字）
   */
  function simpleHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 转换为32位整数
    }
    return Math.abs(hash);
  }

  /**
   * 更新浏览器 favicon
   * @param logoUrl 机构 logo URL，如果为空或无效则使用默认 favicon
   */
  function updateFavicon(logoUrl?: string): void {
    // Safari 兼容性：移除所有旧的 favicon link 标签
    const selectors = [
      "link[rel='icon']",
      "link[rel='shortcut icon']",
      "link[rel='apple-touch-icon']",
      "link[rel='apple-touch-icon-precomposed']"
    ];
    selectors.forEach(selector => {
      const links = document.querySelectorAll(selector);
      links.forEach(link => link.remove());
    });
    
    // 确定最终的 favicon URL
    let finalUrl: string;
    if (logoUrl && logoUrl.trim()) {
      // 如果页面是 HTTPS，但 logoUrl 是 HTTP，则转换为 HTTPS（避免 Mixed Content 错误）
      finalUrl = logoUrl.trim();
      if (window.location.protocol === 'https:' && finalUrl.startsWith('http://')) {
        finalUrl = finalUrl.replace('http://', 'https://');
      }
      
      // Safari 兼容性：确保使用绝对 URL
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://') && !finalUrl.startsWith('data:')) {
        finalUrl = new URL(finalUrl, window.location.origin).href;
      }
      
      // 生成版本号：基于 logoUrl 的 hash，只有当 logo 真正改变时版本号才会改变
      // 如果 URL 本身已经包含版本号参数（如 v=xxx），则保留；否则添加基于内容的版本号
      const urlObj = new URL(finalUrl);
      if (!urlObj.searchParams.has('v')) {
        const version = simpleHash(finalUrl);
        urlObj.searchParams.set('v', version.toString());
        finalUrl = urlObj.toString();
      }
    } else {
      finalUrl = defaultFavicon;
    }
    
    // 根据 URL 推断 type
    let iconType = 'image/x-icon';
    
    if (finalUrl.includes('.png')) {
      iconType = 'image/png';
    } else if (finalUrl.includes('.jpg') || finalUrl.includes('.jpeg')) {
      iconType = 'image/jpeg';
    } else if (finalUrl.includes('.svg')) {
      iconType = 'image/svg+xml';
    }
    
    // 创建多个尺寸的 favicon link 标签（即使图片服务器只提供一个尺寸，浏览器也会自动缩放）
    // 不同浏览器和设备会根据自己的需求选择最合适的尺寸
    const faviconSizes = [
      { sizes: '16x16' },   // 标准小图标
      { sizes: '32x32' },   // 标准图标
      { sizes: '48x48' },   // 某些浏览器需要
      { sizes: '64x64' },   // 高分辨率显示
    ];
    
    faviconSizes.forEach(({ sizes }) => {
      const faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.type = iconType;
      faviconLink.setAttribute('sizes', sizes);
      faviconLink.href = finalUrl;
      document.head.appendChild(faviconLink);
    });
    
    // Safari 兼容性：同时添加 shortcut icon（不带 sizes，兼容旧版浏览器）
    const shortcutLink = document.createElement('link');
    shortcutLink.rel = 'shortcut icon';
    shortcutLink.type = iconType;
    shortcutLink.href = finalUrl;
    document.head.appendChild(shortcutLink);
    
    // iOS Safari 需要多个尺寸的 apple-touch-icon
    if (logoUrl && logoUrl.trim()) {
      const appleTouchSizes = [
        { sizes: '57x57' },   // 旧版 iOS
        { sizes: '60x60' },   // iOS
        { sizes: '72x72' },   // iPad
        { sizes: '76x76' },   // iPad
        { sizes: '114x114' }, // iPhone Retina
        { sizes: '120x120' }, // iPhone
        { sizes: '144x144' }, // iPad Retina
        { sizes: '152x152' }, // iPad
        { sizes: '167x167' }, // iPad Pro
        { sizes: '180x180' }, // iPhone（推荐尺寸）
      ];
      
      appleTouchSizes.forEach(({ sizes }) => {
        const appleTouchLink = document.createElement('link');
        appleTouchLink.rel = 'apple-touch-icon';
        appleTouchLink.setAttribute('sizes', sizes);
        appleTouchLink.href = finalUrl;
        document.head.appendChild(appleTouchLink);
      });
      
      // 不带 sizes 的 apple-touch-icon（兼容旧版 iOS）
      const appleTouchLinkDefault = document.createElement('link');
      appleTouchLinkDefault.rel = 'apple-touch-icon';
      appleTouchLinkDefault.href = finalUrl;
      document.head.appendChild(appleTouchLinkDefault);
    }
  }

  /**
   * 同时更新标题和 favicon
   * @param orgName 机构名称
   * @param logoUrl 机构 logo URL
   */
  function updateDocumentTitleAndFavicon(orgName?: string, logoUrl?: string): void {
    updateDocumentTitle(orgName);
    updateFavicon(logoUrl);
  }

  /**
   * 恢复默认的标题和 favicon
   */
  function resetDocumentTitleAndFavicon(): void {
    updateDocumentTitle();
    updateFavicon();
  }

  return {
    updateDocumentTitle,
    updateFavicon,
    updateDocumentTitleAndFavicon,
    resetDocumentTitleAndFavicon,
  };
}

